import React, { ReactNode } from "react"
import { useSignUpFlow } from "#/components/context/signup/SignUpFlowContext"
import { AppAccessPageLayout } from "#/components/access-page/AppAccessPageLayout"
import { AppAccessPageCard } from "#/components/access-page/AppAccessPageCard"
import { FormattedMessage, useIntl } from "react-intl"
import { Alert, Box, Stack, Typography, useTheme } from "@carrotfertility/carotene-core"
import { useIntlValidator } from "../../utils/hooks/useIntlValidator"
import { emailValidation } from "../../utils/Regexes"
import { Form, FormButton, FormTextField, useFormContext } from "@carrotfertility/carotene-core-x"

interface EmailInputProps {
  enabled: boolean
  responseErrorExists: boolean
}

const EmailInput = ({ enabled, responseErrorExists }: EmailInputProps): JSX.Element => {
  const {
    formState: { errors }
  } = useFormContext()
  const { email } = useSignUpFlow()
  const intlEmailValidation = useIntlValidator(emailValidation)
  const intl = useIntl()

  return (
    <FormTextField
      name="email"
      disabled={!enabled}
      inputProps={{ readOnly: !enabled, disabled: false }}
      registerOptions={{
        required: intl.formatMessage({ defaultMessage: "Required" }),
        validate: intlEmailValidation
      }}
      helperText={errors?.email?.message?.toString()}
      error={Boolean(errors.email) || responseErrorExists}
      label={intl.formatMessage({ defaultMessage: "Email" })}
      defaultValue={email}
      type="email"
      id="email"
      // eslint-disable-next-line jsx-a11y/no-autofocus
      autoFocus
    />
  )
}

type SignUpFindAccountSuccessPageProps = {
  onSubmit: ({ email }: { email: any }) => Promise<void>
  getTranslatedErrorMessage: (errorCode: string) => string | ReactNode | (string | ReactNode)[]
  error: string
  loading: boolean
  heading?: string
  subheading?: string
}

type AddEmailToAccountCardProps = SignUpFindAccountSuccessPageProps
function AddEmailToAccountCard({
  onSubmit,
  error,
  getTranslatedErrorMessage,
  heading,
  subheading
}: AddEmailToAccountCardProps): JSX.Element {
  const { email } = useSignUpFlow()
  const intl = useIntl()
  const headingCopy =
    heading ||
    (email
      ? intl.formatMessage({ defaultMessage: "Finally, tell us how to reach you" })
      : intl.formatMessage({ defaultMessage: "Tell us how to reach you" }))
  const theme = useTheme()

  return (
    <AppAccessPageCard>
      <Form onSubmit={onSubmit} reValidateMode="onSubmit">
        <Stack spacing={theme.spacing(theme.tokens.spacing.lg)}>
          <Typography variant="h2" component="h1">
            {headingCopy}
          </Typography>
          {subheading && <Typography variant="body1">{subheading}</Typography>}
          {error && (
            <Alert severity="error" data-testid="error-alert">
              {getTranslatedErrorMessage(error)}
            </Alert>
          )}
          <EmailInput enabled responseErrorExists={Boolean(error)} />
          <Box display="block" sx={{ alignSelf: "center" }}>
            <FormButton type="submit">
              <FormattedMessage defaultMessage="Continue" />
            </FormButton>
          </Box>
        </Stack>
      </Form>
    </AppAccessPageCard>
  )
}

export default function SignUpFindAccountSuccessPage(props: SignUpFindAccountSuccessPageProps): JSX.Element {
  const intl = useIntl()
  return (
    <AppAccessPageLayout title={intl.formatMessage({ defaultMessage: "Tell us how to reach you" })}>
      <AddEmailToAccountCard {...props} />
    </AppAccessPageLayout>
  )
}
